// Full visit-to-won funnel. Top half is GA4 (sessions, newsletter
// signups, booked calls in the selected range); bottom half is the live
// pipeline (new prospects + closed-won in the same date window via
// computeFunnelBottom). Bar widths use the mockup's fixed ladder — the
// real ratios span orders of magnitude, so proportional bars would
// collapse every step below sessions into slivers.

import { useMemo } from 'react';
import { usePipeline } from '../../../context/PipelineContext';
import { computeFunnelBottom } from '../attributionMath';
import { rangeWindow } from '../dates';
import { useMarketing } from '../MarketingContext';
import { Card, Eyebrow } from '../ui';
import { formatCurrency } from '../../../utils/formatting';

const WIDTHS = [100, 64, 48, 34, 24];
const COLORS = ['#022889', '#123a8f', '#2596BE', '#3EAAD0', '#006D78'];

function pct(part, whole) {
  if (!whole || whole <= 0 || part == null) return null;
  return `${((part / whole) * 100).toFixed(part / whole < 0.1 ? 1 : 0)}%`;
}

export default function FunnelCard() {
  const { prospects } = usePipeline();
  const { range, funnel } = useMarketing();

  const win = useMemo(() => rangeWindow(range), [range]);
  const bottom = useMemo(() => computeFunnelBottom(prospects, win), [prospects, win]);

  const ga4 = funnel.data && funnel.data.configured !== false ? funnel.data : null;
  const rangeLabel = range === 'qtd' ? 'QTD' : range;

  function metaPct(part, whole, label) {
    const p = pct(part, whole);
    return p ? { pct: p, label } : null;
  }

  const steps = [
    { name: 'Website sessions', value: ga4?.sessions, display: ga4 ? ga4.sessions.toLocaleString() : '—', meta: 'Google Analytics' },
    { name: 'Newsletter signups', value: ga4?.signups, display: ga4 ? ga4.signups.toLocaleString() : '—', meta: ga4 ? metaPct(ga4.signups, ga4.sessions, 'of sessions') : 'GA4' },
    { name: 'Booked calls', value: ga4?.calls, display: ga4 ? ga4.calls.toLocaleString() : '—', meta: ga4 ? metaPct(ga4.calls, ga4.signups, 'of signups') : 'GA4' },
    { name: 'New prospects', value: bottom.newProspects, display: bottom.newProspects.toLocaleString(), meta: ga4 ? metaPct(bottom.newProspects, ga4.calls, 'of calls') : 'CRM' },
    {
      name: 'Closed-won',
      value: bottom.wonCount,
      display: bottom.wonValue > 0 ? `${bottom.wonCount} · ${formatCurrency(bottom.wonValue)}` : String(bottom.wonCount),
      meta: metaPct(bottom.wonCount, bottom.newProspects, 'of prospects') || 'CRM',
    },
  ];

  return (
    <Card lash="navy" className="p-4">
      <div className="flex items-center justify-between mb-3.5">
        <Eyebrow>Full funnel · visit to closed-won</Eyebrow>
        <span className="text-ink-3 text-[12px]">last {rangeLabel}</span>
      </div>

      {funnel.loading && <div className="text-[12px] text-ink-3 mb-2">Loading GA4 conversions…</div>}
      {!funnel.loading && !ga4 && (
        <div className="text-[12px] text-ink-2 mb-2">
          {funnel.error ? `GA4 funnel unavailable: ${funnel.error}` : 'GA4 not configured; showing the CRM half only.'}
        </div>
      )}

      <div className="flex flex-col gap-2">
        {steps.map((s, i) => (
          <div key={s.name} className="grid grid-cols-[150px_1fr_130px] items-center gap-3.5">
            <div className="text-[12.5px] font-bold text-navy">{s.name}</div>
            <div
              className="h-[29px] rounded-[5px] flex items-center pl-3 text-white font-black text-[12.5px] tabular-nums"
              style={{ width: `${WIDTHS[i]}%`, backgroundColor: COLORS[i], minWidth: '52px' }}
            >
              {s.display}
            </div>
            <div className="text-[11.5px] text-ink-2 text-right">
              {s.meta && typeof s.meta === 'object'
                ? <><b className="text-deep-teal">{s.meta.pct}</b> {s.meta.label}</>
                : s.meta}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-3.5 bg-bullogic-blue/5 border border-dashed border-bullogic-blue/40 rounded-[7px] px-3 py-2 text-[12px] text-ink-2">
        Top half is Google Analytics; bottom half is the live CRM over the same dates.
        Stitching a website booking to its prospect (Calendly webhook) is the open link, planned post-v1.
      </div>
    </Card>
  );
}
